import { ApiResult, apiUrl } from '../../../api';
import { getAccessToken } from '../../../store/authEffect';
import fetchWithRetry from '../../../util/fetchWithRetry';

// DTOs based on OpenAPI schema - Enhanced with extended versions
export interface BoardDto {
  boardId: string;
  projectExtensionId: string;
  title: string;
  projectExtensionType: ProjectExtensionType;
  isUserBoard: boolean;
  url?: string;
  tasks?: TaskDto[];
  lastUpdatedAt: string;
  boardUrl?: string;
  buckets?: BucketDto[];
}

export interface ExtendedBoardDto extends BoardDto {
  projectId?: string;
  projectName?: string;
  projectNumber?: string;
}

export interface TaskDto {
  id: string;
  boardId: string;
  title: string;
  bucket?: string;
  startDate?: string;
  dueDate?: string;
  status: string;
  priority: string;
  assignees: string[];
  labels: string[];
  url?: string;
  lastUpdatedAt: string;
  boardUrl?: string;
  projectExtensionId?: string;
  projectExtensionType?: string;
  createdBy?: string;
  createdAt: string;
}

export interface ExtendedTaskDto extends TaskDto {
  boardTitle?: string;
  boardProjectExtensionId?: string;
  projectId?: string;
  projectName?: string;
  projectNumber?: string;
  projectDisplayName?: string; // Computed field: shortName || name
}

export interface BucketDto {
  id: string;
  boardId: string;
  name: string;
}

export interface PlannerBoardDto {
  planId: string;
  projectExtensionId: string;
}

export interface ToDoListDto {
  id: string;
  title: string;
}

export interface TaskHubRequestDto {
  plannerBoards?: PlannerBoardDto[];
  toDoLists?: ToDoListDto[];
}

export type ProjectExtensionType =
  | 'sharedMailbox'
  | 'sharepoint'
  | 'privateGroup'
  | 'planner'
  | 'openExperience'
  | 'proman'
  | 'url'
  | 'jira'
  | 'unknown';

// Get tasks from given boards by TaskHubRequestDto
export const apiGetTaskHubTasks = async (
  requestData: TaskHubRequestDto
): Promise<ApiResult<BoardDto[]>> => {
  const result = await fetchWithRetry(`${apiUrl}/collaboration/Taskhub`, {
    method: 'POST',
    headers: {
      Authorization: `Bearer ${await getAccessToken()}`,
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(requestData),
  });

  const data =
    result.status >= 200 && result.status < 300 ? await result.json() : null;

  return {
    result,
    data,
  };
};

// Helper function to get all tasks from boards - now returns ExtendedTaskDto
export const getAllTasksFromBoards = (
  boards: ExtendedBoardDto[]
): ExtendedTaskDto[] => {
  return boards.flatMap((board) =>
    (board.tasks || []).map((task) => ({
      ...task,
      boardTitle: board.title,
      boardProjectExtensionId: board.projectExtensionId,
      projectId: board.projectId,
      projectName: board.projectName,
      projectNumber: board.projectNumber,
      projectDisplayName: board.projectName || 'Unknown Project',
    }))
  );
};

// Main function to fetch boards for projects with enrichment
export const apiFetchBoardsForProjects = async (
  projectIds: string[],
  signal?: AbortSignal
): Promise<ApiResult<ExtendedBoardDto[]>> => {
  try {
    // Build request payload
    const plannerBoards: PlannerBoardDto[] = projectIds.map((projectId) => ({
      planId: projectId,
      projectExtensionId: projectId,
    }));

    const requestPayload: TaskHubRequestDto = {
      plannerBoards,
      toDoLists: [],
    };

    // Use the base API function
    const response = await apiGetTaskHubTasks(requestPayload);

    if (!response.result.ok) {
      throw new Error(
        `API Error: ${response.result.status} ${response.result.statusText}`
      );
    }

    // Transform data to ExtendedBoardDto - enrichment should happen here
    // Note: The actual enrichment with project names should be done in the calling code
    // since this API layer doesn't have access to project information
    const boards = response.data || [];
    const transformedBoards: ExtendedBoardDto[] = boards.map((board) => ({
      ...board,
      boardId: board.boardId || board.projectExtensionId || '',
      title: board.title || 'Untitled Board',
      tasks: (board.tasks || []).map((task) => ({
        ...task,
        assignees: task.assignees || [],
        labels: task.labels || [],
        status: task.status || 'notstarted',
        priority: task.priority || 'medium',
        bucket: task.bucket || 'Default',
      })),
    }));

    return {
      ...response,
      data: transformedBoards,
    };
  } catch (error: any) {
    console.error('TaskHub API - Error fetching boards:', error);

    const failedResult = {
      status: 500,
      statusText: error.message || 'Internal Server Error',
      ok: false,
    } as Response;

    return {
      result: failedResult,
      data: null,
    };
  }
};

// Enhanced fetch function that returns enriched data
export const apiGetTasksForProjectsEnhanced = async (
  projectIds: string[]
): Promise<
  ApiResult<{ boards: ExtendedBoardDto[]; tasks: ExtendedTaskDto[] }>
> => {
  try {
    const boardsResponse = await apiFetchBoardsForProjects(projectIds);

    if (!boardsResponse.result.ok) {
      throw new Error(
        `Failed to fetch boards: ${boardsResponse.result.statusText}`
      );
    }

    const boards = boardsResponse.data || [];
    const tasks = getAllTasksFromBoards(boards);

    return {
      ...boardsResponse,
      data: { boards, tasks },
    };
  } catch (error: any) {
    console.error('TaskHub API - Error in enhanced fetch:', error);

    const failedResult = {
      status: 500,
      statusText: error.message || 'Internal Server Error',
      ok: false,
    } as Response;

    return {
      result: failedResult,
      data: null,
    };
  }
};
